import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { CATEGORY_LABELS, fmtDate, brandLogo } from "@/lib/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Boxes, ChevronRight, Camera, Sparkles, Loader2 } from "lucide-react";

const CATS = ["product", "equipment", "property", "insurance", "contract"];
const EMPTY = { name: "", category: "product", brand: "", model: "", serial: "", purchase_date: "",
  price: "", retailer: "", repairer: "", warranty_months: "", location: "", notes: "", image_url: "", image_path: "" };

function ItemThumb({ item }) {
  const [imgOk, setImgOk] = useState(true);
  const [logoOk, setLogoOk] = useState(true);
  if (item.image_url && imgOk) {
    return <img src={`${API}${item.image_url}`} alt={item.name} onError={() => setImgOk(false)}
      className="w-10 h-10 rounded-xl object-cover border border-white/10" />;
  }
  const logo = brandLogo(item.brand);
  if (logo && logoOk) {
    return <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center p-1 border border-white/10">
      <img src={logo} alt={item.brand} onError={() => setLogoOk(false)} className="max-w-full max-h-full object-contain" /></div>;
  }
  return <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center"><Boxes size={18} className="text-primary" /></div>;
}

export default function Memoire() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [preview, setPreview] = useState("");
  const [form, setForm] = useState(EMPTY);
  const fileRef = useRef(null);

  const load = () => api.get("/items").then((r) => setItems(r.data.filter((i) => i.category !== "vehicle")));
  useEffect(() => { load(); }, []);

  const resetForm = () => { setForm(EMPTY); setPreview(""); };

  const save = async () => {
    if (!form.name) return toast.error("Le nom est requis");
    const payload = { ...form,
      price: form.price ? parseFloat(form.price) : null,
      warranty_months: form.warranty_months ? parseInt(form.warranty_months) : null };
    await api.post("/items", payload);
    toast.success("Bien ajouté à ta mémoire OMY");
    setOpen(false);
    resetForm();
    load();
  };

  const onPhoto = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setPreview(URL.createObjectURL(f));
    setAnalyzing(true);
    setOpen(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/items/recognize", fd);
      const s = data.suggestion || {};
      setForm((prev) => ({ ...prev,
        name: s.name || prev.name,
        category: CATS.includes(s.category) ? s.category : prev.category,
        brand: s.brand || "",
        model: s.model || "",
        serial: s.serial || "",
        warranty_months: s.warranty_months != null ? String(s.warranty_months) : "",
        notes: s.notes || "",
        image_url: data.image_url || "",
        image_path: data.image_path || "" }));
      toast.success("OMY a reconnu ton équipement ✨");
    } catch (err) {
      toast.error("Reconnaissance impossible. Complète manuellement.");
    } finally { setAnalyzing(false); }
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="space-y-6">
      <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={onPhoto}
        className="hidden" data-testid="item-photo-input" />
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Mes biens</h1>
          <p className="text-muted-foreground text-sm mt-1">Chaque bien a son passeport, sa timeline et ses garanties.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="add-item-photo-btn" onClick={() => fileRef.current?.click()}
            className="bg-primary hover:bg-primary/90 rounded-full">
            <Camera size={16} className="mr-1.5" /> Ajouter par photo
          </Button>
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-item-btn" variant="outline" className="rounded-full">
                <Plus size={16} className="mr-1" /> Manuel
              </Button>
            </DialogTrigger>
            <DialogContent className="omy-glass max-w-lg max-h-[88vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Ajouter un bien</DialogTitle></DialogHeader>
              {(preview || analyzing) && (
                <div className="flex items-center gap-3 rounded-2xl bg-black/25 border border-white/10 p-3" data-testid="item-photo-preview">
                  {preview && <img src={preview} alt="" className="w-16 h-16 rounded-xl object-cover" />}
                  <div className="text-sm">
                    {analyzing ? (
                      <span className="flex items-center gap-2 text-primary"><Loader2 size={15} className="animate-spin" /> OMY analyse la photo…</span>
                    ) : (
                      <span className="flex items-center gap-2 text-emerald-400"><Sparkles size={15} /> Référence suggérée — vérifie et enregistre</span>
                    )}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Nom *</Label>
                  <Input data-testid="item-name-input" value={form.name} onChange={upd("name")} className="mt-1 bg-black/30" placeholder="Téléviseur Samsung" /></div>
                <div><Label>Catégorie</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="item-category-select" className="mt-1 bg-black/30"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{CATEGORY_LABELS[c]}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><Label>Marque</Label><Input data-testid="item-brand-input" value={form.brand} onChange={upd("brand")} className="mt-1 bg-black/30" /></div>
                <div><Label>Modèle</Label><Input data-testid="item-model-input" value={form.model} onChange={upd("model")} className="mt-1 bg-black/30" /></div>
                <div><Label>N° de série</Label><Input value={form.serial} onChange={upd("serial")} className="mt-1 bg-black/30" /></div>
                <div><Label>Date d'achat</Label><Input type="date" value={form.purchase_date} onChange={upd("purchase_date")} className="mt-1 bg-black/30" /></div>
                <div><Label>Prix (€)</Label><Input type="number" value={form.price} onChange={upd("price")} className="mt-1 bg-black/30" /></div>
                <div><Label>Revendeur</Label><Input data-testid="item-retailer-input" value={form.retailer} onChange={upd("retailer")} className="mt-1 bg-black/30" placeholder="Boulanger, Amazon…" /></div>
                <div><Label>Réparateur / SAV</Label><Input data-testid="item-repairer-input" value={form.repairer} onChange={upd("repairer")} className="mt-1 bg-black/30" placeholder="En cas de souci" /></div>
                <div><Label>Garantie (mois)</Label><Input type="number" value={form.warranty_months} onChange={upd("warranty_months")} className="mt-1 bg-black/30" /></div>
                <div><Label>Emplacement</Label><Input value={form.location} onChange={upd("location")} className="mt-1 bg-black/30" placeholder="Salon" /></div>
                <div className="col-span-2"><Label>Notes</Label><Input value={form.notes} onChange={upd("notes")} className="mt-1 bg-black/30" /></div>
              </div>
              <DialogFooter>
                <Button data-testid="item-save-btn" onClick={save} disabled={analyzing} className="bg-primary hover:bg-primary/90 rounded-full">Enregistrer</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {items.length === 0 && <p className="text-muted-foreground text-sm">Aucun bien pour l'instant. Prends une photo, ajoute-en un ou parle à OMY.</p>}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((it, i) => (
          <button key={it.id} data-testid={`item-card-${i}`} onClick={() => navigate(`/app/memoire/${it.id}`)}
            className="omy-glass rounded-2xl p-5 text-left hover:border-primary/40 hover:-translate-y-1 transition-all group">
            <div className="flex items-center justify-between">
              <ItemThumb item={it} />
              <ChevronRight size={16} className="text-muted-foreground group-hover:translate-x-1 transition-transform" />
            </div>
            <h3 className="font-display font-medium mt-3">{it.name}</h3>
            <p className="text-xs text-muted-foreground mt-0.5">{it.brand} {it.model}</p>
            <div className="flex items-center gap-2 mt-3 text-xs text-muted-foreground">
              <span className="font-mono uppercase">{CATEGORY_LABELS[it.category] || it.category}</span>
              {it.purchase_date && <span>· {fmtDate(it.purchase_date)}</span>}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
